import { useCallback, useEffect, useMemo, useState } from 'react';
import { StandardDataGrid, Toast, authHeaders, gridActionButton, s } from './listPageStyles';
import { RuleCategoryPage } from './RuleCategoryPage';
import { RulesPage } from './RulesPage';

const EMPTY_WORKFLOW_FORM = {
  name: '',
  description: '',
  isActive: true,
};

const DATA_TYPE_OPTIONS = [
  { value: '1', label: 'String' },
  { value: '2', label: 'Number' },
  { value: '3', label: 'Decimal' },
  { value: '4', label: 'Boolean' },
  { value: '5', label: 'Date' },
];

const workflowTabs = [
  { key: 'workflows', label: 'Workflow Yönetimi' },
  { key: 'ruleCategories', label: 'Kural Kategorileri' },
  { key: 'rules', label: 'Kural Yönetimi' },
];

const workflowTabStyles = {
  tabBar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  tab: { minHeight: 36, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-color, #d1d5db)', background: 'var(--bg-card, #fff)', color: 'var(--text-secondary, #4b5563)', cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  tabActive: { background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' },
};

function compareValues(left, right, direction) {
  const a = left ?? '';
  const b = right ?? '';
  const result = String(a).localeCompare(String(b), 'tr', { sensitivity: 'base' });
  return direction === 'asc' ? result : -result;
}

function createRowKey() {
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function createDraftInput(overrides = {}) {
  return {
    id: null,
    rowKey: createRowKey(),
    fieldName: '',
    dataType: '1',
    description: '',
    isRequired: false,
    isActive: true,
    defaultValue: '',
    ...overrides,
  };
}

function mapInputToDraft(item) {
  return createDraftInput({
    id: item.id,
    rowKey: item.id,
    fieldName: item.fieldName || '',
    dataType: String(item.dataType ?? 1),
    description: item.description || '',
    isRequired: Boolean(item.isRequired),
    isActive: item.isActive ?? true,
    defaultValue: item.defaultValue || '',
  });
}

function getDataTypeLabel(value) {
  return DATA_TYPE_OPTIONS.find(option => option.value === String(value))?.label || '-';
}

function normalizeFieldName(value) {
  return (value || '').trim().toLocaleLowerCase('tr-TR');
}

function getApiMessage(payload, fallbackMessage) {
  if (!payload) return fallbackMessage;

  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }

  if (payload.Message || payload.message) {
    return payload.Message || payload.message;
  }

  const errorEntries = payload.errors && typeof payload.errors === 'object'
    ? Object.entries(payload.errors)
        .flatMap(([key, values]) => (Array.isArray(values) ? values.map(value => `${key}: ${value}`) : []))
        .filter(Boolean)
    : [];

  if (errorEntries.length > 0) {
    return errorEntries.join(' | ');
  }

  if (payload.detail || payload.title) {
    return [payload.title, payload.detail].filter(Boolean).join(' - ');
  }

  return fallbackMessage;
}

async function getResponseError(response, fallbackMessage, context = '') {
  let rawText = '';

  try {
    rawText = await response.text();
  } catch {
    rawText = '';
  }

  let payload = null;
  if (rawText) {
    try {
      payload = JSON.parse(rawText);
    } catch {
      payload = rawText;
    }
  }

  const message = getApiMessage(payload, fallbackMessage);
  const prefix = context ? `${context}: ` : '';
  const statusInfo = `HTTP ${response.status}`;

  if (message && message !== fallbackMessage) {
    return `${prefix}${message} (${statusInfo})`;
  }

  if (typeof payload === 'string' && payload.trim()) {
    const compactText = payload.trim().slice(0, 220);
    return `${prefix}${fallbackMessage} (${statusInfo}) - ${compactText}`;
  }

  return `${prefix}${fallbackMessage} (${statusInfo})`;
}

export function WorkflowManagementPageLegacy() {
  const [activeDefinition, setActiveDefinition] = useState('workflows');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowModal, setWorkflowModal] = useState(null);
  const [workflowForm, setWorkflowForm] = useState(EMPTY_WORKFLOW_FORM);
  const [workflowDeleteConfirm, setWorkflowDeleteConfirm] = useState(false);
  const [draftInputs, setDraftInputs] = useState([]);
  const [persistedInputIds, setPersistedInputIds] = useState([]);
  const [deletedInputIds, setDeletedInputIds] = useState([]);
  const [inputsLoading, setInputsLoading] = useState(false);
  const [workflowSaving, setWorkflowSaving] = useState(false);
  const [editingRowKey, setEditingRowKey] = useState(null);
  const [editingRowDraft, setEditingRowDraft] = useState(null);
  const [toast, setToast] = useState(null);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/workflows', { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json = await res.json();
      const data = Array.isArray(json.data ?? json) ? (json.data ?? json) : [];
      setRows(data);
      return data;
    } catch (error) {
      showToast('error', `Workflow listesi yuklenemedi: ${error.message}`);
      return [];
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const loadWorkflowInputs = useCallback(async (workflowId) => {
    if (!workflowId) {
      setDraftInputs([]);
      setDeletedInputIds([]);
      return;
    }

    setInputsLoading(true);
    try {
      const res = await fetch(`/api/workflows/${workflowId}/inputs`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json = await res.json();
      const data = Array.isArray(json.data ?? json) ? (json.data ?? json) : [];
      setDraftInputs(data.map(mapInputToDraft));
      setPersistedInputIds(data.map(item => item.id).filter(Boolean));
      setDeletedInputIds([]);
      setEditingRowKey(null);
      setEditingRowDraft(null);
    } catch (error) {
      setDraftInputs([]);
      setPersistedInputIds([]);
      setDeletedInputIds([]);
      showToast('error', `Saha tanimlari yuklenemedi: ${error.message}`);
    } finally {
      setInputsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  useEffect(() => {
    if (workflowModal === 'edit' && selectedWorkflow?.id) {
      loadWorkflowInputs(selectedWorkflow.id);
    }

    if (workflowModal === 'new') {
      setDraftInputs([]);
      setPersistedInputIds([]);
      setDeletedInputIds([]);
      setEditingRowKey(null);
      setEditingRowDraft(null);
      setInputsLoading(false);
    }
  }, [workflowModal, selectedWorkflow, loadWorkflowInputs]);

  const filteredRows = useMemo(() => {
    const q = filterText.trim().toLocaleLowerCase('tr-TR');
    if (!q) return rows;

    return rows.filter(row =>
      (row.name || '').toLocaleLowerCase('tr-TR').includes(q) ||
      (row.description || '').toLocaleLowerCase('tr-TR').includes(q)
    );
  }, [filterText, rows]);

  const sortFieldKey = sortField ? sortField.charAt(0).toLowerCase() + sortField.slice(1) : '';
  const sortedRows = useMemo(() => (
    [...filteredRows].sort((a, b) => compareValues(a[sortFieldKey], b[sortFieldKey], sortDir))
  ), [filteredRows, sortDir, sortFieldKey]);

  const totalCount = sortedRows.length;
  const skip = (currentPage - 1) * pageSize;
  const visibleRows = sortedRows.slice(skip, skip + pageSize);

  const closeWorkflowModal = () => {
    if (workflowSaving) return;
    setWorkflowModal(null);
    setEditingRowKey(null);
    setEditingRowDraft(null);
    setPersistedInputIds([]);
    setDeletedInputIds([]);
  };

  const openNewWorkflow = () => {
    setSelectedWorkflow(null);
    setWorkflowForm(EMPTY_WORKFLOW_FORM);
    setDraftInputs([]);
    setPersistedInputIds([]);
    setDeletedInputIds([]);
    setEditingRowKey(null);
    setEditingRowDraft(null);
    setWorkflowModal('new');
  };

  const openEditWorkflow = (row = selectedWorkflow) => {
    if (!row) return;

    setSelectedWorkflow(row);
    setWorkflowForm({
      name: row.name || '',
      description: row.description || '',
      isActive: Boolean(row.isActive),
    });
    setWorkflowModal('edit');
  };

  const beginEditRow = (row) => {
    setEditingRowKey(row.rowKey);
    setEditingRowDraft({
      rowKey: row.rowKey,
      fieldName: row.fieldName || '',
      dataType: String(row.dataType ?? '1'),
      description: row.description || '',
      isRequired: Boolean(row.isRequired),
      isActive: row.isActive ?? true,
      defaultValue: row.defaultValue || '',
    });
  };

  const handleAddInputRow = () => {
    if (editingRowKey) {
      showToast('error', 'Lutfen acik olan saha satirini kaydedin veya iptal edin.');
      return;
    }

    const newRow = createDraftInput();
    setDraftInputs(current => [newRow, ...current]);
    beginEditRow(newRow);
  };

  const handleCancelRowEdit = () => {
    const currentRow = draftInputs.find(row => row.rowKey === editingRowKey);
    if (currentRow && !currentRow.id && !currentRow.fieldName.trim() && !currentRow.description.trim() && !currentRow.defaultValue.trim()) {
      setDraftInputs(rowsState => rowsState.filter(row => row.rowKey !== editingRowKey));
    }

    setEditingRowKey(null);
    setEditingRowDraft(null);
  };

  const handleSaveRowEdit = () => {
    if (!editingRowDraft) return;

    const fieldName = editingRowDraft.fieldName.trim();
    if (!fieldName) {
      showToast('error', 'Saha adi zorunludur.');
      return;
    }

    const duplicate = draftInputs.some(row =>
      row.rowKey !== editingRowDraft.rowKey &&
      normalizeFieldName(row.fieldName) === normalizeFieldName(fieldName)
    );

    if (duplicate) {
      showToast('error', 'Ayni workflow icin ayni saha adi tekrar edemez.');
      return;
    }

    setDraftInputs(current => current.map(row => (
      row.rowKey === editingRowDraft.rowKey
        ? {
            ...row,
            fieldName,
            dataType: String(editingRowDraft.dataType),
            description: editingRowDraft.description.trim(),
            isRequired: Boolean(editingRowDraft.isRequired),
            isActive: Boolean(editingRowDraft.isActive),
            defaultValue: editingRowDraft.defaultValue.trim(),
          }
        : row
    )));
    setEditingRowKey(null);
    setEditingRowDraft(null);
  };

  const handleDeleteInputRow = (row) => {
    if (row.id) {
      setDeletedInputIds(current => current.includes(row.id) ? current : [...current, row.id]);
    }

    setDraftInputs(current => current.filter(item => item.rowKey !== row.rowKey));
    if (editingRowKey === row.rowKey) {
      setEditingRowKey(null);
      setEditingRowDraft(null);
    }
  };

  const validateDraftInputs = () => {
    if (editingRowKey) {
      return 'Lutfen duzenlenen saha satirini once kaydedin veya iptal edin.';
    }

    const seen = new Set();
    for (const row of draftInputs) {
      const fieldName = row.fieldName.trim();
      if (!fieldName) {
        return 'Tum saha satirlarinda saha adi dolu olmalidir.';
      }

      const normalized = normalizeFieldName(fieldName);
      if (seen.has(normalized)) {
        return 'Ayni workflow icin ayni saha adi tekrar edemez.';
      }

      seen.add(normalized);
    }

    return null;
  };

  const syncInputDefinitions = async (workflowId) => {
    const idsToDelete = Array.from(new Set([...persistedInputIds, ...deletedInputIds])).filter(Boolean);

    for (const deletedId of idsToDelete) {
      const deleteResponse = await fetch(`/api/workflows/${workflowId}/inputs/${deletedId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!deleteResponse.ok) {
        throw new Error(await getResponseError(deleteResponse, 'Saha tanimlari silinirken hata olustu.', 'Saha silme'));
      }
    }

    for (const row of draftInputs) {
      const params = new URLSearchParams({
        fieldName: row.fieldName.trim(),
        dataType: String(parseInt(row.dataType, 10)),
        description: row.description.trim(),
        isRequired: String(Boolean(row.isRequired)),
        isActive: String(Boolean(row.isActive)),
        defaultValue: row.defaultValue.trim(),
      });

      const response = await fetch(`/api/workflows/${workflowId}/inputs?${params.toString()}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!response.ok) {
        throw new Error(await getResponseError(
          response,
          'Saha tanimlari kaydedilirken hata olustu.',
          `Saha "${row.fieldName.trim()}"`
        ));
      }
    }
  };

  const handleWorkflowSave = async () => {
    if (!workflowForm.name.trim()) {
      showToast('error', 'Workflow adi zorunludur.');
      return;
    }

    const validationError = validateDraftInputs();
    if (validationError) {
      showToast('error', validationError);
      return;
    }

    setWorkflowSaving(true);
    try {
      const isEdit = workflowModal === 'edit';
      const workflowUrl = isEdit ? `/api/workflows/${selectedWorkflow.id}` : '/api/workflows';
      const workflowResponse = await fetch(workflowUrl, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          name: workflowForm.name.trim(),
          description: workflowForm.description.trim(),
          isActive: workflowForm.isActive,
        }),
      });
      let workflowJson = null;
      try {
        workflowJson = await workflowResponse.json();
      } catch {
        workflowJson = null;
      }
      if (!workflowResponse.ok) {
        throw new Error(getApiMessage(workflowJson, `Workflow kaydedilemedi. (HTTP ${workflowResponse.status})`));
      }

      const workflowId = isEdit ? selectedWorkflow.id : (workflowJson?.Data?.id || workflowJson?.data?.id);
      if (!workflowId) {
        throw new Error('Workflow kimligi alinamadi.');
      }

      await syncInputDefinitions(workflowId);

      const latestRows = await fetchWorkflows();
      const refreshedWorkflow = latestRows.find(row => row.id === workflowId) || {
        id: workflowId,
        name: workflowForm.name.trim(),
        description: workflowForm.description.trim(),
        isActive: workflowForm.isActive,
      };

      setSelectedWorkflow(refreshedWorkflow);
      setWorkflowModal(null);
      setPersistedInputIds([]);
      setDeletedInputIds([]);
      setEditingRowKey(null);
      setEditingRowDraft(null);
      showToast('success', 'Workflow ve saha tanimlari kaydedildi.');
    } catch (error) {
      showToast('error', error.message || 'Workflow kaydedilemedi.');
    } finally {
      setWorkflowSaving(false);
    }
  };

  const handleWorkflowDelete = async () => {
    if (!selectedWorkflow?.id) return;

    try {
      const res = await fetch(`/api/workflows/${selectedWorkflow.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      let json = null;
      try {
        json = await res.json();
      } catch {
        json = null;
      }
      if (!res.ok) throw new Error(getApiMessage(json, `Workflow silinemedi. (HTTP ${res.status})`));

      setWorkflowDeleteConfirm(false);
      setSelectedWorkflow(null);
      setDraftInputs([]);
      setPersistedInputIds([]);
      setDeletedInputIds([]);
      setEditingRowKey(null);
      setEditingRowDraft(null);
      await fetchWorkflows();
      showToast('success', 'Workflow silindi.');
    } catch (error) {
      setWorkflowDeleteConfirm(false);
      showToast('error', error.message || 'Workflow silinemedi.');
    }
  };

  const renderWorkflowTabs = () => (
    <div style={workflowTabStyles.tabBar}>
      {workflowTabs.map(tab => (
        <button
          key={tab.key}
          type="button"
          style={{ ...workflowTabStyles.tab, ...(activeDefinition === tab.key ? workflowTabStyles.tabActive : {}) }}
          onClick={() => setActiveDefinition(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  const columns = [
    { field: 'name', headerName: 'Workflow', flex: 1, minWidth: 180, renderCell: (params) => <strong>{params.row.name}</strong> },
    { field: 'description', headerName: 'Açıklama', flex: 1.5, minWidth: 220, renderCell: (params) => params.row.description || '-' },
    {
      field: 'isActive',
      headerName: 'Durum',
      width: 130,
      renderCell: (params) => (
        <span style={params.row.isActive ? s.statusActive : s.statusPassive}>
          {params.row.isActive ? '● Aktif' : '○ Pasif'}
        </span>
      ),
    },
    {
      field: 'usage',
      headerName: 'Kullanım',
      width: 220,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <span style={{ color: 'var(--text-secondary)' }}>
          {(params.row.inputCount ?? 0)} saha | {(params.row.ruleCount ?? 0)} kural | {(params.row.testCaseCount ?? 0)} test
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: 'İşlemler',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: '100%' }}>
          <button style={{ ...gridActionButton, background: 'var(--warn)' }} onClick={(event) => { event.stopPropagation(); openEditWorkflow(params.row); }}>Değiştir</button>
          <button style={{ ...gridActionButton, background: '#E53E3E', minWidth: 48 }} onClick={(event) => { event.stopPropagation(); setSelectedWorkflow(params.row); setWorkflowDeleteConfirm(true); }}>Sil</button>
        </div>
      ),
    },
  ];

  if (activeDefinition === 'ruleCategories') {
    return (
      <div style={s.container}>
        {renderWorkflowTabs()}
        <RuleCategoryPage />
      </div>
    );
  }

  if (activeDefinition === 'rules') {
    return (
      <div style={s.container}>
        {renderWorkflowTabs()}
        <RulesPage />
      </div>
    );
  }

  return (
    <div style={s.container}>
      {renderWorkflowTabs()}
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h2 style={s.pageTitle}>Workflow Yonetimi</h2>
          <p style={s.pageSubtitle}>Workflow ve saha tanimlarini ayni popup icinde toplu olarak yonetin.</p>
        </div>
        <button style={s.btnPrimary} onClick={openNewWorkflow}>+ Yeni Workflow</button>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <input
            style={s.filterInput}
            type="text"
            placeholder="Workflow adi ile filtrele..."
            value={filterInput}
            onChange={event => setFilterInput(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                setFilterText(filterInput);
                setCurrentPage(1);
              }
            }}
          />
          {filterText && (
            <button
              style={s.btnSecondary}
              onClick={() => {
                setFilterInput('');
                setFilterText('');
                setCurrentPage(1);
              }}
            >
              Temizle
            </button>
          )}
          <button style={s.btnSecondary} onClick={fetchWorkflows}>Yenile</button>
        </div>
      </div>

      <StandardDataGrid
        rows={visibleRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={page => setCurrentPage(page)}
        onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, direction) => {
          setCurrentPage(1);
          setSortField(field || 'name');
          setSortDir(direction || 'asc');
        }}
        selectedId={selectedWorkflow?.id}
        onRowClick={row => setSelectedWorkflow(row)}
        onRowDoubleClick={row => openEditWorkflow(row)}
        exportFileName="workflow-grid"
      />

      {workflowModal && (
        <div style={s.overlay} onClick={closeWorkflowModal}>
          <div style={{ ...s.modal, maxWidth: 1120 }} onClick={event => event.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {workflowModal === 'new' ? 'Yeni Workflow' : 'Workflow Duzenle'}
              </h3>
              <button style={s.closeBtn} onClick={closeWorkflowModal}>x</button>
            </div>
            <div style={{ ...s.modalBody, maxHeight: '72vh' }}>
              <div style={s.formGroup}>
                <label style={s.label}>Workflow Adi *</label>
                <input
                  style={s.input}
                  value={workflowForm.name}
                  onChange={event => setWorkflowForm(current => ({ ...current, name: event.target.value }))}
                  placeholder="Workflow adini giriniz"
                />
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Aciklama</label>
                  <input
                    style={s.input}
                    value={workflowForm.description}
                    onChange={event => setWorkflowForm(current => ({ ...current, description: event.target.value }))}
                    placeholder="Workflow aciklamasi"
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <select
                    style={s.select}
                    value={workflowForm.isActive ? 'true' : 'false'}
                    onChange={event => setWorkflowForm(current => ({ ...current, isActive: event.target.value === 'true' }))}
                  >
                    <option value="true">Aktif</option>
                    <option value="false">Pasif</option>
                  </select>
                </div>
              </div>

              <div style={styles.modalSection}>
                <div style={styles.sectionHeader}>
                  <div>
                    <div style={styles.sectionEyebrow}>Workflow Input Definitions</div>
                    <div style={styles.sectionTitle}>Saha Tanimlari</div>
                    <div style={styles.sectionMeta}>
                      Saha satirlarini bu gridde hazirlayin. Tum satirlar workflow ile birlikte tek kayitta saklanir.
                    </div>
                  </div>
                  <button
                    style={editingRowKey ? s.btnDisabled : s.btnSecondary}
                    onClick={handleAddInputRow}
                    disabled={Boolean(editingRowKey)}
                  >
                    Yeni Saha
                  </button>
                </div>

                <div style={styles.gridWrapper}>
                  {inputsLoading ? (
                    <div style={styles.emptyState}>Saha tanimlari yukleniyor...</div>
                  ) : (
                    <table style={styles.gridTable}>
                      <thead>
                        <tr>
                          <th style={styles.gridHeader}>Saha Adi</th>
                          <th style={styles.gridHeader}>Veri Tipi</th>
                          <th style={styles.gridHeader}>Varsayilan Deger</th>
                          <th style={styles.gridHeader}>Aciklama</th>
                          <th style={styles.gridHeader}>Zorunlu</th>
                          <th style={styles.gridHeader}>Aktif</th>
                          <th style={styles.gridHeader}>Islemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {draftInputs.length === 0 ? (
                          <tr>
                            <td colSpan={7} style={styles.emptyGridCell}>
                              Henuz saha tanimi yok. Yeni Saha ile satir ekleyebilirsiniz.
                            </td>
                          </tr>
                        ) : draftInputs.map(row => {
                          const isEditing = editingRowKey === row.rowKey;

                          return (
                            <tr key={row.rowKey}>
                              <td style={styles.gridCell}>
                                {isEditing ? (
                                  <input
                                    style={styles.gridInput}
                                    value={editingRowDraft?.fieldName || ''}
                                    onChange={event => setEditingRowDraft(current => ({ ...current, fieldName: event.target.value }))}
                                    placeholder="Orn. MusteriAdi"
                                  />
                                ) : row.fieldName}
                              </td>
                              <td style={styles.gridCell}>
                                {isEditing ? (
                                  <select
                                    style={styles.gridSelect}
                                    value={editingRowDraft?.dataType || '1'}
                                    onChange={event => setEditingRowDraft(current => ({ ...current, dataType: event.target.value }))}
                                  >
                                    {DATA_TYPE_OPTIONS.map(option => (
                                      <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                  </select>
                                ) : getDataTypeLabel(row.dataType)}
                              </td>
                              <td style={styles.gridCell}>
                                {isEditing ? (
                                  <input
                                    style={styles.gridInput}
                                    value={editingRowDraft?.defaultValue || ''}
                                    onChange={event => setEditingRowDraft(current => ({ ...current, defaultValue: event.target.value }))}
                                    placeholder="Opsiyonel"
                                  />
                                ) : (row.defaultValue || '-')}
                              </td>
                              <td style={styles.gridCell}>
                                {isEditing ? (
                                  <input
                                    style={styles.gridInput}
                                    value={editingRowDraft?.description || ''}
                                    onChange={event => setEditingRowDraft(current => ({ ...current, description: event.target.value }))}
                                    placeholder="Alan amaci"
                                  />
                                ) : (row.description || '-')}
                              </td>
                              <td style={styles.gridCellCenter}>
                                {isEditing ? (
                                  <input
                                    type="checkbox"
                                    checked={Boolean(editingRowDraft?.isRequired)}
                                    onChange={event => setEditingRowDraft(current => ({ ...current, isRequired: event.target.checked }))}
                                  />
                                ) : (row.isRequired ? 'Evet' : 'Hayir')}
                              </td>
                              <td style={styles.gridCellCenter}>
                                {isEditing ? (
                                  <input
                                    type="checkbox"
                                    checked={Boolean(editingRowDraft?.isActive)}
                                    onChange={event => setEditingRowDraft(current => ({ ...current, isActive: event.target.checked }))}
                                  />
                                ) : (row.isActive ? 'Aktif' : 'Pasif')}
                              </td>
                              <td style={styles.gridActions}>
                                {isEditing ? (
                                  <>
                                    <button style={s.btnSmallInfo} onClick={handleSaveRowEdit}>Kaydet</button>
                                    <button style={s.btnSecondary} onClick={handleCancelRowEdit}>Iptal</button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      style={editingRowKey ? s.btnDisabled : s.btnSmallWarning}
                                      onClick={() => beginEditRow(row)}
                                      disabled={Boolean(editingRowKey)}
                                    >
                                      Degistir
                                    </button>
                                    <button style={s.btnSmallDanger} onClick={() => handleDeleteInputRow(row)}>Sil</button>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button
                style={workflowSaving ? s.btnDisabled : s.btnPrimary}
                onClick={handleWorkflowSave}
                disabled={workflowSaving}
              >
                {workflowSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
              <button style={s.btnSecondary} onClick={closeWorkflowModal}>Iptal</button>
            </div>
          </div>
        </div>
      )}

      {workflowDeleteConfirm && (
        <div style={s.overlay} onClick={() => setWorkflowDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={event => event.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayi</h3>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, lineHeight: 1.6 }}>
                <strong>{selectedWorkflow?.name}</strong> workflow kaydini silmek istediginize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleWorkflowDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setWorkflowDeleteConfirm(false)}>Iptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

const styles = {
  modalSection: {
    marginTop: 12,
    border: '1px solid var(--border-color)',
    borderRadius: 14,
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    padding: '16px 18px',
    borderBottom: '1px solid var(--border-color)',
    background: 'linear-gradient(90deg, rgba(0,115,234,0.08) 0%, rgba(0,115,234,0.02) 100%)',
  },
  sectionEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: 'var(--text-secondary)',
  },
  sectionTitle: {
    marginTop: 4,
    fontSize: 16,
    fontWeight: 800,
    color: 'var(--text-primary)',
  },
  sectionMeta: {
    marginTop: 4,
    fontSize: 13,
    color: 'var(--text-secondary)',
  },
  gridWrapper: {
    overflowX: 'auto',
  },
  gridTable: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 980,
  },
  gridHeader: {
    padding: '12px 14px',
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    borderBottom: '1px solid var(--border-color)',
    background: 'rgba(0,115,234,0.03)',
  },
  gridCell: {
    padding: '12px 14px',
    borderBottom: '1px solid var(--border-color)',
    color: 'var(--text-primary)',
    verticalAlign: 'middle',
  },
  gridCellCenter: {
    padding: '12px 14px',
    borderBottom: '1px solid var(--border-color)',
    color: 'var(--text-primary)',
    verticalAlign: 'middle',
    textAlign: 'center',
  },
  gridActions: {
    padding: '12px 14px',
    borderBottom: '1px solid var(--border-color)',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
  gridInput: {
    width: '100%',
    minWidth: 120,
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid var(--border-color)',
    background: 'var(--input-bg)',
    color: 'var(--text-primary)',
    fontSize: 13,
    boxSizing: 'border-box',
  },
  gridSelect: {
    width: '100%',
    minWidth: 120,
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid var(--border-color)',
    background: 'var(--input-bg)',
    color: 'var(--text-primary)',
    fontSize: 13,
    boxSizing: 'border-box',
  },
  emptyState: {
    padding: 24,
    color: 'var(--text-secondary)',
    fontSize: 14,
  },
  emptyGridCell: {
    padding: 28,
    textAlign: 'center',
    color: 'var(--text-secondary)',
    fontSize: 14,
  },
};
